import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MenuKebab } from "../../icons/menuKebab";
import { colors } from "../../colors";
import { DropdownProps } from "./Dropdown.types";
import DropdownContainer from "./DropdownContainer";

export function Dropdown(props: DropdownProps) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);

    const updatePortalPosition = () => {
        if (!triggerRef.current || !portalRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const menuWidth = portalRef.current.offsetWidth || 240;
        const menuHeight = portalRef.current.offsetHeight || 0;
        const verticalOffset = 8;

        const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUpward = menuHeight > 0 && spaceBelow < (menuHeight + verticalOffset) && spaceAbove > spaceBelow;
        const top = shouldOpenUpward
            ? Math.max(8, rect.top - menuHeight - verticalOffset)
            : rect.bottom + verticalOffset;

        portalRef.current.style.left = `${left}px`;
        portalRef.current.style.top = `${top}px`;
    };

    const toggleMenu = () => {
        const nextOpen = !isMenuOpen;
        setIsMenuOpen(nextOpen);

        if (nextOpen && props.portaled) {
            requestAnimationFrame(updatePortalPosition);
        }
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const clickedInsideTrigger = !!menuRef.current?.contains(target);
        const clickedInsidePortal = !!portalRef.current?.contains(target);

        if (!clickedInsideTrigger && !clickedInsidePortal) {
            closeMenu();
        }
    };

    useEffect(() => {
        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            if (props.portaled) {
                window.addEventListener('resize', updatePortalPosition);
                window.addEventListener('scroll', updatePortalPosition, true);
                requestAnimationFrame(updatePortalPosition);
            }
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
            if (props.portaled) {
                window.removeEventListener('resize', updatePortalPosition);
                window.removeEventListener('scroll', updatePortalPosition, true);
            }
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (props.portaled) {
                window.removeEventListener('resize', updatePortalPosition);
                window.removeEventListener('scroll', updatePortalPosition, true);
            }
        };
    }, [isMenuOpen, props.portaled]);

    return (
        <div ref={menuRef} className="relative">
            <button ref={triggerRef} onClick={e => { toggleMenu() }} className="focus:outline-none" >
                <MenuKebab color={colors.brandPrimary[700]} size={20} />
            </button>
            {
                isMenuOpen && !props.portaled &&
                <DropdownContainer
                    onClose={closeMenu}
                    items={props.items}
                />
            }
            {
                isMenuOpen && props.portaled && createPortal(
                    <div ref={portalRef} className="fixed z-[1200]">
                        <DropdownContainer
                            onClose={closeMenu}
                            items={props.items}
                            portaled
                        />
                    </div>,
                    document.body
                )
            }
        </div>
    )
}